// 标签生成服务 - 根据内容自动生成标签
import { RealAIService } from './realAiService.js'

/**
 * 标签生成服务类
 */
class TagGeneratorService {
  
  /**
   * 预定义的标签类别和标签池
   */
  static TAG_CATEGORIES = {
    // 题材/类型标签
    genre: [
      '科幻', '奇幻', '玄幻', '修仙', '武侠', '历史', '现代', '未来',
      '都市', '校园', '职场', '军事', '战争', '冒险', '探险', '推理',
      '悬疑', '恐怖', '惊悚', '浪漫', '爱情', '青春', '喜剧', '轻松',
      '治愈', '温馨', '日常', '末世', '废土', '蒸汽朋克', '赛博朋克',
      '魔法', '超能力', '异能', '机甲', '星际', '穿越', '重生', '系统',
      '宫斗', '权谋', '商战', '竞技', '游戏', '虚拟现实'
    ],
    
    // 风格标签
    style: [
      '轻松', '沉重', '黑暗', '光明', '幽默', '严肃', '诙谐', '悲伤',
      '欢乐', '励志', '热血', '温暖', '残酷', '现实', '理想', '讽刺',
      '唯美', '粗犷', '细腻', '宏大', '微观', '史诗', '传奇'
    ],
    
    // 主题标签
    theme: [
      '友情', '爱情', '亲情', '成长', '救赎', '复仇', '正义', '邪恶',
      '自由', '束缚', '追梦', '奋斗', '牺牲', '背叛', '忠诚', '信仰',
      '希望', '绝望', '选择', '命运', '人性', '道德', '伦理', '生存',
      '死亡', '时间', '记忆', '身份', '真相', '谎言'
    ],
    
    // 特色标签（用于故事，世界模拟器不使用）
    feature: [
      '战斗向', '养成', '收集', '探索', '解谜', '策略',
      '即时', '回合制', '文字冒险', '视觉小说', '模拟经营'
    ],
    
    // 世界特性标签（仅用于世界模拟器，描述特殊机制）
    worldFeature: [
      '回合制', '即时制',
      '养成系统', '收集系统', '探索系统', '解谜系统',
      '策略系统', '战斗系统', '社交系统', '经济系统'
    ],
    
    // 角色特性标签（仅用于角色）
    characterTraits: [
      '温柔', '活泼', '高冷', '腹黑', '傲娇', '天然', '霸道', '热血',
      '中二', '成熟', '冷酷', '甜美', '御姐', '萝莉', '女王', '软萌', '知性'
    ],
    
    // 受众标签
    audience: [
      '全年龄', '青少年', '成人', '儿童', '女性向', '男性向',
      '新手友好', '硬核', '休闲', '重度'
    ]
  }

  /**
   * 世界模拟器通用特性标签（不应使用）
   * 这些是所有世界模拟器都具备的特性，不应作为标签
   */
  static WORLD_SIMULATOR_COMMON_FEATURES = [
    '选择分支', '多结局', '互动性强', '剧情向', '角色扮演'
  ]

  /**
   * 使用AI分析内容并生成标签
   * @param {string} contentType - 内容类型: 'story', 'character', 'world'
   * @param {object} content - 内容对象
   * @returns {Promise<string[]>} 生成的标签数组
   */
  static async generateTagsWithAI(contentType, content) {
    try {
      console.log(`🏷️ 开始为 ${contentType} 生成标签...`)
      
      // 构建分析文本
      let analysisText = ''
      
      if (contentType === 'story') {
        analysisText = `
标题: ${content.title || ''}
描述: ${content.description || ''}
题材: ${content.genre || ''}
风格: ${content.style || ''}
主题: ${content.theme || ''}
内容摘要: ${content.metadata?.summary || ''}
        `.trim()
      } else if (contentType === 'character') {
        analysisText = `
角色名: ${content.name || ''}
描述: ${content.description || ''}
性格: ${content.personality || ''}
背景: ${content.background || ''}
兴趣: ${content.interests?.join(', ') || ''}
        `.trim()
      } else if (contentType === 'world') {
        analysisText = `
世界名: ${content.name || ''}
描述: ${content.description || ''}
摘要: ${content.summary || ''}
世界观: ${content.preText?.substring(0, 500) || ''}
类型: ${content.type || ''}
风格: ${content.style?.join(', ') || ''}
        `.trim()
      }

      // 使用简化的关键词提取（如果AI不可用）
      const keywordTags = this.extractKeywordTags(analysisText, contentType)
      
      // 尝试使用AI生成更精准的标签
      try {
        // 根据内容类型构建不同的提示词
        let availableTags = ''
        let excludeRules = ''
        
        if (contentType === 'world') {
          // 世界模拟器：排除通用特性，使用世界特性标签
          availableTags = `
题材类型: ${this.TAG_CATEGORIES.genre.join(', ')}
风格: ${this.TAG_CATEGORIES.style.join(', ')}
主题: ${this.TAG_CATEGORIES.theme.join(', ')}
世界特性: ${this.TAG_CATEGORIES.worldFeature.join(', ')}
受众: ${this.TAG_CATEGORIES.audience.join(', ')}`
          excludeRules = `
重要：世界模拟器本身已具备"选择分支"、"多结局"、"互动性强"、"剧情向"、"角色扮演"等特性，这些不应作为标签。
只选择真正能区分这个世界的独特标签，如题材、风格、主题、特殊机制等。`
        } else if (contentType === 'character') {
          // 角色：优先使用角色特性标签
          availableTags = `
角色特性: ${this.TAG_CATEGORIES.characterTraits.join(', ')}
题材类型: ${this.TAG_CATEGORIES.genre.join(', ')}
受众: ${this.TAG_CATEGORIES.audience.join(', ')}`
          excludeRules = `
优先选择角色特性标签（性格特点），其次选择背景题材，最后选择受众。`
        } else {
          // 故事：使用所有标签类型
          availableTags = `
题材类型: ${this.TAG_CATEGORIES.genre.join(', ')}
风格: ${this.TAG_CATEGORIES.style.join(', ')}
主题: ${this.TAG_CATEGORIES.theme.join(', ')}
特色: ${this.TAG_CATEGORIES.feature.join(', ')}
受众: ${this.TAG_CATEGORIES.audience.join(', ')}`
          excludeRules = ''
        }
        
        const prompt = `分析以下${contentType === 'story' ? '故事' : contentType === 'character' ? '角色' : '世界模拟器'}的内容，从以下标签池中选择5-8个最合适的标签。

内容信息：
${analysisText}

可用标签池：
${availableTags}
${excludeRules}

请直接返回5-8个最合适的标签，用逗号分隔，不要有其他解释。
${contentType === 'world' ? '例如：科幻,冒险,热血,策略系统,全年龄' : contentType === 'character' ? '例如：温柔,活泼,校园,女性向' : '例如：科幻,冒险,热血,回合制,全年龄' }`

        const aiResponse = await RealAIService.callLanguageModel(prompt, {
          temperature: 0.3,
          maxTokens: 100
        })
        
        // 解析AI响应
        let aiTags = aiResponse
          .split(/[,，、\n]/)
          .map(tag => tag.trim())
          .filter(tag => tag && tag.length > 0 && tag.length < 10)
          .slice(0, 8)
        
        // 如果是世界模拟器，过滤掉通用特性标签
        if (contentType === 'world') {
          aiTags = aiTags.filter(tag => 
            !this.WORLD_SIMULATOR_COMMON_FEATURES.includes(tag)
          )
        }
        
        if (aiTags.length >= 3) {
          console.log(`✅ AI生成标签成功:`, aiTags)
          return aiTags
        }
      } catch (aiError) {
        console.warn('⚠️ AI标签生成失败，使用关键词提取:', aiError)
      }
      
      // 如果AI失败，返回关键词标签
      console.log(`✅ 使用关键词提取标签:`, keywordTags)
      return keywordTags
      
    } catch (error) {
      console.error('❌ 标签生成失败:', error)
      return ['原创', '精选']
    }
  }

  /**
   * 基于关键词匹配提取标签（快速备选方案）
   * @param {string} text - 要分析的文本
   * @param {string} contentType - 内容类型: 'story', 'character', 'world'
   * @returns {string[]} 提取的标签
   */
  static extractKeywordTags(text, contentType = 'story') {
    const lowerText = text.toLowerCase()
    const matchedTags = []
    
    // 根据内容类型选择标签类别
    let categoriesToCheck = []
    
    if (contentType === 'world') {
      // 世界模拟器：不使用feature，使用worldFeature，排除通用特性
      categoriesToCheck = [
        this.TAG_CATEGORIES.genre,
        this.TAG_CATEGORIES.style,
        this.TAG_CATEGORIES.theme,
        this.TAG_CATEGORIES.worldFeature,
        this.TAG_CATEGORIES.audience
      ]
    } else if (contentType === 'character') {
      // 角色：优先角色特性
      categoriesToCheck = [
        this.TAG_CATEGORIES.characterTraits,
        this.TAG_CATEGORIES.genre,
        this.TAG_CATEGORIES.audience
      ]
    } else {
      // 故事：使用所有类别
      categoriesToCheck = [
        this.TAG_CATEGORIES.genre,
        this.TAG_CATEGORIES.style,
        this.TAG_CATEGORIES.theme,
        this.TAG_CATEGORIES.feature,
        this.TAG_CATEGORIES.audience
      ]
    }
    
    // 遍历选定的标签类别
    for (const category of categoriesToCheck) {
      for (const tag of category) {
        // 世界模拟器排除通用特性标签
        if (contentType === 'world' && this.WORLD_SIMULATOR_COMMON_FEATURES.includes(tag)) {
          continue
        }
        
        if (lowerText.includes(tag.toLowerCase()) && !matchedTags.includes(tag)) {
          matchedTags.push(tag)
          if (matchedTags.length >= 8) break
        }
      }
      if (matchedTags.length >= 8) break
    }
    
    // 如果匹配的标签太少，添加一些默认标签
    if (matchedTags.length < 3) {
      const defaultTags = ['原创', '精选', '推荐']
      return [...matchedTags, ...defaultTags.filter(t => !matchedTags.includes(t))].slice(0, 5)
    }
    
    return matchedTags.slice(0, 8)
  }

  /**
   * 为故事生成标签
   * @param {object} story - 故事对象
   * @returns {Promise<string[]>} 生成的标签
   */
  static async generateStoryTags(story) {
    return await this.generateTagsWithAI('story', story)
  }

  /**
   * 为角色生成标签
   * @param {object} character - 角色对象
   * @returns {Promise<string[]>} 生成的标签
   */
  static async generateCharacterTags(character) {
    return await this.generateTagsWithAI('character', character)
  }

  /**
   * 为世界生成标签
   * @param {object} world - 世界对象
   * @returns {Promise<string[]>} 生成的标签
   */
  static async generateWorldTags(world) {
    return await this.generateTagsWithAI('world', world)
  }

  /**
   * 获取所有可用标签（用于标签选择器）
   * @returns {object} 标签类别对象
   */
  static getAllTags() {
    return this.TAG_CATEGORIES
  }

  /**
   * 验证标签是否有效
   * @param {string} tag - 标签
   * @returns {boolean} 是否有效
   */
  static isValidTag(tag) {
    if (!tag || typeof tag !== 'string' || tag.length === 0 || tag.length > 10) {
      return false
    }
    
    // 检查是否在预定义标签池中
    for (const category of Object.values(this.TAG_CATEGORIES)) {
      if (category.includes(tag)) {
        return true
      }
    }
    
    // 允许自定义标签（但有长度限制）
    return tag.length >= 2 && tag.length <= 6
  }

  /**
   * 清理和规范化标签
   * @param {string[]} tags - 原始标签数组
   * @returns {string[]} 清理后的标签数组
   */
  static normalizeTags(tags) {
    if (!Array.isArray(tags)) return []
    
    return tags
      .map(tag => typeof tag === 'string' ? tag.trim() : '')
      .filter(tag => this.isValidTag(tag))
      .slice(0, 10) // 最多10个标签
      .filter((tag, index, self) => self.indexOf(tag) === index) // 去重
  }
}

export default TagGeneratorService

